import { useEffect, useMemo, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Instagram, MapPin, Menu, Send, X } from "lucide-react";
import { motion } from "framer-motion";
import { loadSiteData } from "./lib/siteData";
import { classNames, formatPrice } from "./lib/format";
import { createReservation } from "./lib/reservations";
import type { Category, MenuItem, SiteData } from "./types";
import { AdminApp } from "./components/AdminApp";

type VisualVersion = "v1" | "v2" | "v3";

function useSiteData() {
  const [data, setData] = useState<SiteData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSiteData()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

function Header() {
  const [open, setOpen] = useState(false);
  const links = [
    ["Konzept", "#konzept"],
    ["Speisekarte", "#speisekarte"],
    ["Reservierung", "#reservierung"],
    ["Kontakt", "#kontakt"]
  ];
  const navigateToSection = (event: MouseEvent<HTMLAnchorElement>, href: string) => {
    event.preventDefault();
    const target = document.querySelector<HTMLElement>(href);
    const header = document.querySelector<HTMLElement>(".site-header");

    if (target) {
      const headerHeight = header?.offsetHeight ?? 0;
      const contentOffset = Number.parseFloat(window.getComputedStyle(target).paddingTop) || 0;
      const targetTop = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16 - contentOffset;
      window.scrollTo({ behavior: "smooth", top: Math.max(0, targetTop) });
    }

    window.history.pushState(null, "", href);
    setOpen(false);
  };

  return (
    <header className="site-header">
      <a className="brand" href="#top" aria-label="Ortaya Startseite">
        Ortaya
      </a>
      <nav className={classNames("nav", open && "nav-open")}>
        {links.map(([label, href]) => (
          <a key={href} href={href} onClick={(event) => navigateToSection(event, href)}>
            {label}
          </a>
        ))}
      </nav>
      <a
        className="header-social"
        href="https://www.instagram.com/ortaya_kitchen/"
        target="_blank"
        rel="noreferrer"
        aria-label="Ortaya Kitchen auf Instagram"
      >
        <Instagram size={18} />
      </a>
      <button className="icon-button" onClick={() => setOpen((value) => !value)} aria-label="Menü öffnen">
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>
    </header>
  );
}

function VersionSwitcher({ value, onChange }: { value: VisualVersion; onChange: (value: VisualVersion) => void }) {
  return (
    <label className="version-switcher">
      <span>Ansicht {value.toUpperCase()}</span>
      <select aria-label="Ansicht" value={value} onChange={(event) => onChange(event.target.value as VisualVersion)}>
        <option value="v1">V1</option>
        <option value="v2">V2</option>
        <option value="v3">V3</option>
      </select>
    </label>
  );
}

function Hero({ data }: { data: SiteData }) {
  return (
    <section className="hero" id="top">
      <div className="hero-line-art" aria-label="Illustration eines gedeckten Tisches bei Ortaya" />
      <motion.div
        className="hero-content"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <p className="eyebrow">Ortaya Kitchen</p>
        <h1 className="hero-menu-title">Mezze · Grill · Drinks</h1>
        <p className="hero-title">{data.restaurant.heroSubtitle}</p>
        <p className="hero-copy">{data.restaurant.heroTitle}</p>
        <div className="hero-actions">
          <a className="button button-primary" href="#reservierung">
            Tisch reservieren
          </a>
          <a className="button button-secondary" href="#speisekarte">
            Speisekarte ansehen
          </a>
          <span className="hero-next">Für gemeinsame Abende</span>
        </div>
      </motion.div>
    </section>
  );
}

function Concept() {
  return (
    <section className="section concept" id="konzept">
      <div className="section-kicker">Konzept</div>
      <div className="concept-grid layout-grid">
        <h2 className="layout-span-5">Bei Ortaya beginnt alles in der Mitte des Tisches.</h2>
        <div className="layout-span-6 layout-start-7">
          <p>
            Unsere Küche bringt Mezze, Grillgerichte, frischen Fisch und Drinks zusammen.
            Nicht als steife Reihenfolge, sondern als gemeinsamer Abend: probieren,
            teilen, nachbestellen, weiterreden.
          </p>
          <p>
            Anatolische Aromen treffen auf eine moderne Bar- und Tischkultur mitten in
            Bielefeld. Warm, gesellig und mit genau der Ruhe, die ein langer Abend braucht.
          </p>
        </div>
      </div>
    </section>
  );
}

function ImageStory() {
  return (
    <section className="image-story" aria-label="Ortaya Mood">
      <figure className="story-poster">
        <img src="/assets/instagram/ig-01.png" alt="Ortaya Mezze & Drinks Grafik" />
      </figure>
      <div className="story-copy">
        <p className="eyebrow">Ortaya</p>
        <h2>Was auf den Tisch kommt, gehört in die Mitte.</h2>
        <p>
          Mezze, Grill, Fisch und Drinks kommen zusammen. Die Küche ist für lange
          Abende gemacht: bestellen, probieren, weiterreichen und noch etwas dazu nehmen.
        </p>
      </div>
      <figure className="story-food">
        <img src="/assets/instagram/ig-10.png" alt="Mezze auf Fladenbrot bei Ortaya" />
      </figure>
    </section>
  );
}

function V3EditorialGallery() {
  const images = [
    ["/assets/reference/ortaya-editorial-01.jpeg", "Tisch mit Mezze bei Ortaya"],
    ["/assets/reference/ortaya-editorial-02.jpg", "Ortaya Eingang"],
    ["/assets/reference/ortaya-editorial-03.jpeg", "Anrichten in der Ortaya Küche"],
    ["/assets/reference/ortaya-editorial-04.jpg", "Mezze auf einem Teller"],
    ["/assets/reference/ortaya-editorial-05.jpeg", "Anatolische Spezialitäten"],
    ["/assets/reference/ortaya-editorial-06.jpg", "Frische Mezze auf dem Tisch"],
    ["/assets/reference/ortaya-editorial-07.jpeg", "Gericht aus der Ortaya Küche"],
    ["/assets/reference/ortaya-editorial-08.jpg", "Gegrillter Fisch"],
    ["/assets/reference/ortaya-editorial-09.jpeg", "Mezze und Salat zum Teilen"],
    ["/assets/reference/ortaya-editorial-10.jpg", "Drink bei Ortaya"],
    ["/assets/reference/ortaya-editorial-11.jpeg", "Anrichten eines Getränks"],
    ["/assets/instagram/ig-10.png", "Mezze von Ortaya Kitchen in Bielefeld"]
  ];

  return (
    <section className="v3-editorial-gallery" aria-label="Eindrücke aus der Ortaya Küche">
      <div className="v3-gallery-grid">
        {images.map(([src, alt]) => (
          <figure key={src}>
            <img src={src} alt={alt} loading="lazy" />
          </figure>
        ))}
      </div>
    </section>
  );
}

function getDietaryMarkers(item: MenuItem) {
  const attributes = new Set((item.attributes ?? []).map((attribute) => attribute.trim().toLocaleLowerCase("de-DE")));

  return [
    attributes.has("vegetarisch") ? { code: "v", label: "vegetarisch" } : null,
    attributes.has("vegan") ? { code: "v+", label: "vegan" } : null,
    attributes.has("glutenfrei") ? { code: "g", label: "glutenfrei" } : null
  ].filter((marker): marker is { code: string; label: string } => marker !== null);
}

function MenuCard({ item }: { item: MenuItem }) {
  const dietaryMarkers = getDietaryMarkers(item);

  return (
    <article className={classNames("menu-card", item.featured && "menu-card-featured")}>
      <div className="menu-card-head">
        <h3>
          {item.title}
          {dietaryMarkers.length ? (
            <span className="menu-dietary-markers" aria-label={dietaryMarkers.map((marker) => marker.label).join(", ")}>
              {dietaryMarkers.map((marker) => <span key={marker.code}>{marker.code}</span>)}
            </span>
          ) : null}
        </h3>
        {item.variants.length === 1 && <strong>{formatPrice(item.variants[0].price)}</strong>}
      </div>
      {item.description && <p>{item.description}</p>}
      {item.variants.length > 1 && (
        <ul className="variants">
          {item.variants.map((variant) => (
            <li key={variant.id}>
              <span>{variant.label || "Standard"}</span>
              <strong>{formatPrice(variant.price)}</strong>
            </li>
          ))}
        </ul>
      )}
      {item.extras?.length ? (
        <div className="extras">
          <span>Extras</span>
          {item.extras.map((extra) => (
            <small key={extra.id}>
              {extra.label} +{formatPrice(extra.price)}
            </small>
          ))}
        </div>
      ) : null}
      {item.attributes?.length ? (
        <details className="menu-details">
          <summary>Hinweise & Allergene <ChevronDown size={16} /></summary>
          <p>{item.attributes.join(" · ")}</p>
        </details>
      ) : null}
    </article>
  );
}

function MenuCategoryGroup({
  category,
  categories,
  menuItems,
  depth,
  openCategoryByParent,
  onToggle
}: {
  category: Category;
  categories: Category[];
  menuItems: MenuItem[];
  depth: number;
  openCategoryByParent: Record<string, string>;
  onToggle: (parentKey: string, categoryId: string) => void;
}) {
  const children = categories.filter((item) => item.parentId === category.id);
  const items = menuItems.filter((item) => item.categoryIds.includes(category.id));
  const parentKey = category.parentId ?? "root";
  const isOpen = openCategoryByParent[parentKey] === category.id;
  const hasOpenSibling = !isOpen && Boolean(openCategoryByParent[parentKey]);

  if (!children.length && !items.length) return null;

  function handleHeadingClick(button: HTMLButtonElement) {
    if (isOpen) {
      onToggle(parentKey, category.id);
      window.setTimeout(() => {
        const parentHeading = category.parentId
          ? document.querySelector<HTMLElement>(`[data-category-id="${category.parentId}"] > .menu-category-heading`)
          : null;
        const target = parentHeading ?? document.querySelector<HTMLElement>("#speisekarte");
        const headerHeight = document.querySelector<HTMLElement>(".site-header")?.offsetHeight ?? 0;
        const top = target
          ? window.scrollY + target.getBoundingClientRect().top - headerHeight - 20
          : 0;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }, 300);
      return;
    }

    onToggle(parentKey, category.id);
    const alignOpenedHeading = () => {
      const headerHeight = document.querySelector<HTMLElement>(".site-header")?.offsetHeight ?? 0;
      const top = window.scrollY + button.getBoundingClientRect().top - headerHeight - 20;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    if (hasOpenSibling) {
      window.setTimeout(alignOpenedHeading, 300);
    } else {
      window.requestAnimationFrame(alignOpenedHeading);
    }
  }

  return (
    <section
      className={classNames("menu-category-group", `menu-category-depth-${depth}`, isOpen && "is-open")}
      data-category-id={category.id}
    >
      <button
        className="menu-category-heading"
        type="button"
        aria-expanded={isOpen}
        onClick={(event) => handleHeadingClick(event.currentTarget)}
      >
        <span>
          <h3>{category.title}</h3>
          {category.description && category.id !== "54616-kalte-mezze" && <p>{category.description}</p>}
        </span>
        <ChevronDown className="menu-category-chevron" size={20} aria-hidden="true" />
      </button>
      <div className="menu-category-content">
        <div className="menu-category-reveal">
          {items.length ? (
            <div className="menu-grid">
              {items.map((item) => <MenuCard item={item} key={item.id} />)}
            </div>
          ) : null}
          {children.map((child) => (
            <MenuCategoryGroup
              category={child}
              categories={categories}
              menuItems={menuItems}
              depth={depth + 1}
              openCategoryByParent={openCategoryByParent}
              onToggle={onToggle}
              key={child.id}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function MenuSection({ data, visualVersion }: { data: SiteData; visualVersion: VisualVersion }) {
  const activeCategories = useMemo(
    () => data.categories.filter((category) => category.active).sort((a, b) => a.order - b.order),
    [data.categories]
  );
  const roots = activeCategories.filter((category) => !category.parentId);
  const [selectedRootId, setSelectedRootId] = useState(roots[0]?.id ?? "");
  const [openCategoryByParent, setOpenCategoryByParent] = useState<Record<string, string>>({});
  const [menuPresentation, setMenuPresentation] = useState<"v1" | "v2">("v1");

  useEffect(() => {
    if (!roots.some((category) => category.id === selectedRootId)) {
      setSelectedRootId(roots[0]?.id ?? "");
    }
  }, [roots, selectedRootId]);

  const selectedRoot = roots.find((category) => category.id === selectedRootId);
  const selectedRootItems = useMemo(
    () => data.menuItems.filter((item) => item.active && item.categoryIds.includes(selectedRootId)).sort((a, b) => a.order - b.order),
    [data.menuItems, selectedRootId]
  );
  const selectedRootChildren = activeCategories.filter((category) => category.parentId === selectedRootId);

  function handleCategoryToggle(parentKey: string, categoryId: string) {
    setOpenCategoryByParent((current) => {
      const next = { ...current };
      const descendantIds = new Set<string>();
      const collectDescendants = (id: string) => {
        activeCategories
          .filter((category) => category.parentId === id)
          .forEach((category) => {
            descendantIds.add(category.id);
            collectDescendants(category.id);
          });
      };
      const clearDescendants = (id: string) => {
        collectDescendants(id);
        delete next[id];
        descendantIds.forEach((descendantId) => delete next[descendantId]);
      };

      if (next[parentKey] === categoryId) {
        delete next[parentKey];
        clearDescendants(categoryId);
      } else {
        const previouslyOpen = next[parentKey];
        if (previouslyOpen) clearDescendants(previouslyOpen);
        clearDescendants(categoryId);
        next[parentKey] = categoryId;
      }

      return next;
    });
  }

  return (
    <section
      className={classNames(
        "section",
        "menu-section",
        visualVersion === "v3" && `menu-presentation-${menuPresentation}`
      )}
      id="speisekarte"
    >
      <div className="menu-section-topline">
        <div className="section-kicker">Speisekarte</div>
        {visualVersion === "v3" ? (
          <div className="menu-view-switcher" aria-label="Speisekarte Ansicht">
            <button
              className={classNames(menuPresentation === "v1" && "active")}
              onClick={() => setMenuPresentation("v1")}
              type="button"
            >
              Ansicht V1
            </button>
            <button
              className={classNames(menuPresentation === "v2" && "active")}
              onClick={() => setMenuPresentation("v2")}
              type="button"
            >
              Ansicht V2
            </button>
          </div>
        ) : null}
      </div>
      <div className="section-head">
        <h2>Eine Karte für gemeinsame Abende.</h2>
      </div>
      <div className="menu-section-tabs" aria-label="Speisekarte Bereiche">
        {roots.map((root) => (
          <button
            key={root.id}
            className={classNames(selectedRootId === root.id && "active")}
            type="button"
            onClick={() => {
              setSelectedRootId(root.id);
              setOpenCategoryByParent({});
            }}
          >
            {root.title}
          </button>
        ))}
      </div>
      <div className="menu-hierarchy">
        {selectedRoot && selectedRootItems.length ? (
          <div className="menu-grid menu-root-grid">
            {selectedRootItems.map((item) => <MenuCard item={item} key={item.id} />)}
          </div>
        ) : null}
        {selectedRootChildren.map((category) => (
          <MenuCategoryGroup
            category={category}
            categories={activeCategories}
            menuItems={data.menuItems.filter((item) => item.active).sort((a, b) => a.order - b.order)}
            depth={0}
            openCategoryByParent={openCategoryByParent}
            onToggle={handleCategoryToggle}
            key={category.id}
          />
        ))}
        {!selectedRootItems.length && !selectedRootChildren.length && (
          <p className="menu-empty">Für diese Kategorie sind derzeit keine Einträge veröffentlicht.</p>
        )}
      </div>
      {visualVersion === "v3" && menuPresentation === "v2" ? (
        <footer className="menu-dietary-legend" aria-label="Hinweise zur Speisekarte">
          <p className="menu-allergy-note">Solltest Du Allergene / Unverträglichkeiten haben, sprich uns bitte vorher an.</p>
          <div className="menu-dietary-key">
            <div>
              <span><b>v</b> vegetarisch</span>
              <span><b>v+</b> vegan</span>
              <span><b>g</b> glutenfrei</span>
            </div>
            <p>Alle Preise in €</p>
          </div>
        </footer>
      ) : null}
    </section>
  );
}

const reservationTimes = [
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00",
  "20:30",
  "21:00"
];

const calendarWeekdays = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function dateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateFromKey(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function ReservationDatePicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const selectedDate = value ? dateFromKey(value) : null;
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => selectedDate ?? new Date());
  const today = new Date();
  const todayKey = dateKey(today);
  const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
  const leadingDays = (monthStart.getDay() + 6) % 7;
  const days = Array.from({ length: monthEnd.getDate() }, (_, index) => {
    const date = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), index + 1);
    return { date, key: dateKey(date) };
  });
  const canGoBack = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1) > new Date(today.getFullYear(), today.getMonth(), 1);

  function selectDate(key: string) {
    onChange(key);
    setOpen(false);
  }

  return (
    <div className="reservation-date-field">
      <button
        aria-expanded={open}
        aria-haspopup="dialog"
        className={classNames("reservation-date-trigger", value && "has-value")}
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <span>{selectedDate ? new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(selectedDate) : "Datum wählen"}</span>
        <CalendarDays aria-hidden="true" size={18} />
      </button>
      {open ? (
        <div className="reservation-calendar" role="dialog" aria-label="Datum auswählen">
          <div className="reservation-calendar-head">
            <button
              aria-label="Vorheriger Monat"
              disabled={!canGoBack}
              onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
              type="button"
            >
              <ChevronLeft size={17} />
            </button>
            <strong>{new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric" }).format(visibleMonth)}</strong>
            <button
              aria-label="Nächster Monat"
              onClick={() => setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
              type="button"
            >
              <ChevronRight size={17} />
            </button>
          </div>
          <div className="reservation-calendar-grid" role="grid" aria-label="Kalendertage">
            {calendarWeekdays.map((weekday) => <span key={weekday}>{weekday}</span>)}
            {Array.from({ length: leadingDays }, (_, index) => <i aria-hidden="true" key={`blank-${index}`} />)}
            {days.map(({ date, key }) => {
              const isPast = key < todayKey;
              const isSelected = key === value;
              const isToday = key === todayKey;

              return (
                <button
                  aria-current={isToday ? "date" : undefined}
                  aria-label={new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "long", year: "numeric" }).format(date)}
                  className={classNames(isSelected && "selected", isToday && "today")}
                  disabled={isPast}
                  key={key}
                  onClick={() => selectDate(key)}
                  type="button"
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Reservation() {
  const [form, setForm] = useState({
    name: "",
    persons: "2",
    date: "",
    time: "",
    email: "",
    phone: "",
    note: "",
    privacyAccepted: false
  });
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  function updateField(field: keyof typeof form, value: string | boolean) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.date) {
      setState("error");
      setMessage("Bitte wählt ein Datum für eure Reservierungsanfrage.");
      return;
    }
    setState("submitting");
    setMessage("");

    try {
      await createReservation({
        name: form.name.trim(),
        persons: Number(form.persons),
        date: form.date,
        time: form.time,
        email: form.email.trim(),
        phone: form.phone.trim(),
        note: form.note.trim(),
        privacyAccepted: form.privacyAccepted
      });

      setForm({
        name: "",
        persons: "2",
        date: "",
        time: "",
        email: "",
        phone: "",
        note: "",
        privacyAccepted: false
      });
      setState("success");
      setMessage("Reservierungsanfrage erhalten. Die Reservierung ist erst gültig, wenn wir sie bestätigen.");
    } catch {
      setState("error");
      setMessage("Die Anfrage konnte nicht gesendet werden. Bitte prüft eure Angaben oder ruft uns an.");
    }
  }

  return (
    <section className="reservation reservation-presentation-v2" id="reservierung">
      <div className="reservation-copy">
        <p className="eyebrow">Reservierung</p>
        <div className="reservation-heading-row">
          <h2>Tisch reservieren</h2>
        </div>
        <p>
          Wählt Datum, Uhrzeit und Personenanzahl. Eure Reservierung ist eine Anfrage und
          wird von uns bestätigt.
        </p>
        <p className="reservation-warm-note">Wir halten gern einen Platz für euch frei.</p>
      </div>
      <form className="reservation-form" onSubmit={handleSubmit}>
        <label>
          Name
          <input
            value={form.name}
            onChange={(event) => updateField("name", event.target.value)}
            required
            autoComplete="name"
          />
        </label>
        <label>
          Personen
          <input
            value={form.persons}
            onChange={(event) => updateField("persons", event.target.value)}
            min="1"
            max="20"
            type="number"
            required
          />
        </label>
        <label>
          Datum
          <ReservationDatePicker
            value={form.date}
            onChange={(value) => updateField("date", value)}
          />
        </label>
        <label>
          Uhrzeit
          <select value={form.time} onChange={(event) => updateField("time", event.target.value)} required>
            <option value="">Bitte wählen</option>
            {reservationTimes.map((time) => (
              <option key={time} value={time}>
                {time}
              </option>
            ))}
          </select>
        </label>
        <label>
          E-Mail
          <input
            value={form.email}
            onChange={(event) => updateField("email", event.target.value)}
            type="email"
            required
            autoComplete="email"
          />
        </label>
        <label>
          Telefon
          <input
            value={form.phone}
            onChange={(event) => updateField("phone", event.target.value)}
            type="tel"
            required
            autoComplete="tel"
          />
        </label>
        <label className="reservation-note">
          Anmerkung
          <textarea
            value={form.note}
            onChange={(event) => updateField("note", event.target.value)}
            placeholder="Optional"
          />
        </label>
        <label className="reservation-policy">
          <input
            checked={form.privacyAccepted}
            onChange={(event) => updateField("privacyAccepted", event.target.checked)}
            type="checkbox"
            required
          />
          <span>Ich stimme der Verarbeitung meiner Daten zur Bearbeitung der Reservierungsanfrage zu.</span>
        </label>
        {message && <p className={classNames("reservation-message", state === "error" && "error")}>{message}</p>}
        <button className="button button-primary reservation-submit" disabled={state === "submitting"} type="submit">
          <Send size={18} />
          {state === "submitting" ? "Wird gesendet..." : "Reservierungsanfrage senden"}
        </button>
      </form>
    </section>
  );
}

function Contact({ data }: { data: SiteData }) {
  return (
    <section className="section contact contact-presentation-v2" id="kontakt">
      <div className="contact-card">
        <div className="contact-details">
          <div className="contact-heading">
            <p className="eyebrow">Kontakt</p>
          </div>
          <h2>Ortaya</h2>
          <address>
            <span>{data.restaurant.address}</span>
            <span>{data.restaurant.city}</span>
            <a href={`tel:${data.restaurant.phone.replace(/\s/g, "")}`}>{data.restaurant.phone}</a>
          </address>
          <p className="contact-welcome">Wir freuen uns auf euren Besuch.</p>
        </div>
        <div className="contact-actions">
          <a
            href="https://www.google.com/maps/search/?api=1&query=Karl-Eilers-Stra%C3%9Fe%2020%2033602%20Bielefeld"
            target="_blank"
            rel="noreferrer"
          >
            <MapPin size={18} />
            Route
          </a>
          <a href={data.restaurant.instagramUrl} target="_blank" rel="noreferrer">
            <Instagram size={18} />
            Instagram
          </a>
          <a href={data.restaurant.facebookUrl} target="_blank" rel="noreferrer">
            <ExternalLink size={18} />
            Facebook
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return <footer className="site-footer" aria-label="Seitenende" />;
}

function Playlist() {
  return (
    <section className="playlist-strip" aria-label="Ortaya Playlist">
      <iframe
        src="https://open.spotify.com/embed/playlist/1KMFyHreVxqZxlga5dN52E?utm_source=generator&theme=0"
        title="Ortaya Playlist auf Spotify"
        loading="lazy"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      />
    </section>
  );
}

function PublicSite() {
  const { data, loading } = useSiteData();
  const [visualVersion, setVisualVersion] = useState<VisualVersion>(() => {
    const stored = window.sessionStorage.getItem("ortaya-visual-version");
    return stored === "v1" || stored === "v3" ? stored : "v2";
  });

  useEffect(() => {
    window.sessionStorage.setItem("ortaya-visual-version", visualVersion);
  }, [visualVersion]);

  if (loading || !data) {
    return <div className="loading">Ortaya wird geladen...</div>;
  }

  return (
    <div className={classNames("site-version", `site-version-${visualVersion}`)}>
      <VersionSwitcher value={visualVersion} onChange={setVisualVersion} />
      <Header />
      <main>
        <Hero data={data} />
        <div className="section-atmosphere section-atmosphere-concept"><Concept /></div>
        <div className="section-atmosphere section-atmosphere-story"><ImageStory /></div>
        <V3EditorialGallery />
        <div className="section-atmosphere section-atmosphere-menu"><MenuSection data={data} visualVersion={visualVersion} /></div>
        <div className="section-atmosphere section-atmosphere-reservation"><Reservation /></div>
        <div className="section-atmosphere section-atmosphere-contact"><Contact data={data} /></div>
        <Playlist />
        <Footer />
      </main>
    </div>
  );
}

export default function App() {
  const [adminMode, setAdminMode] = useState(() => window.location.hash === "#admin");

  useEffect(() => {
    const onHashChange = () => setAdminMode(window.location.hash === "#admin");
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  if (adminMode) {
    return <AdminApp onClose={() => (window.location.hash = "#top")} />;
  }

  return <PublicSite />;
}
